/** All-titles Customer Support overview: health strip, title cards, and the queue. */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import {
  TITLE_LABELS, TITLE_ORDER, topicLabel, waitingLabel,
  type DeskBucket, type DeskConversationPage, type DeskSummary,
} from '../../lib/supportDesk'
import { SupportQueue, conversationHref, deskOff, useDeskMe } from './SupportQueue'

function hoursSince(iso: string | null): number | null {
  return iso ? Math.max(0, (Date.now() - new Date(iso).getTime()) / 3_600_000) : null
}

export function SupportOverview() {
  const nav = useNavigate()
  const { canAct } = useDeskMe()
  const [mine, setMine] = useState(false)
  const summary = useQuery<DeskSummary>({
    queryKey: ['support', 'summary'], queryFn: () => api.get<DeskSummary>('/api/support/summary'),
    refetchInterval: 60_000, retry: false,
  })

  // Oldest to-review conversation (the list puts it first) for the health strip and "Review oldest first".
  const oldest = useQuery<DeskConversationPage>({
    queryKey: ['support', 'oldest'],
    queryFn: () => api.get<DeskConversationPage>('/api/support/conversations?state=to_review&limit=1'),
    refetchInterval: 60_000, retry: false, enabled: summary.isSuccess,
  })

  const mineCount = useQuery<DeskConversationPage>({
    queryKey: ['support', 'mine-count'],
    queryFn: () => api.get<DeskConversationPage>(`/api/support/conversations?state=to_review&assignee=me&limit=1`),
    refetchInterval: 60_000, retry: false, enabled: summary.isSuccess && canAct,
  })

  if (deskOff(summary.error)) {
    return (
      <div className="p-8 max-w-[640px]">
        <div className="label mb-2">Customer Support</div>
        <p className="font-serif text-2xl text-paper">Customer Support desk is not switched on</p>
        <p className="mt-2 text-sm text-paper-dim">Ask an administrator to turn it on. The existing inboxes keep working in the meantime.</p>
      </div>
    )
  }

  const s = summary.data
  const buckets = s ? (Object.keys(s.titles) as DeskBucket[]) : []
  const overTarget = buckets.reduce((n, b) => n + (s?.titles[b].over_target ?? 0), 0)
  const first = oldest.data?.items[0]
  const firstHours = first?.waiting_hours ?? hoursSince(s ? buckets.map(b => s.titles[b].oldest_waiting_at).filter(Boolean).sort()[0] ?? null : null)

  return (
    <div className="p-8 space-y-8 max-w-[1200px]">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Members · customer support</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            {s ? s.total : '–'} <span className="text-amber-glow">conversations to review</span>
          </h1>
        </div>
        <div className="flex gap-3">
          {canAct && (
            <button className={`label border px-3 py-2 ${mine ? 'border-amber-glow text-amber-glow' : 'border-ink-500'}`}
              aria-pressed={mine} onClick={() => setMine(v => !v)}>
              Assigned to me · {mineCount.data?.total ?? 0}
            </button>
          )}
          <button className="label border border-ink-500 px-3 py-2 disabled:opacity-40" disabled={!first}
            onClick={() => first && nav(conversationHref(first))}>
            Review oldest first
          </button>
        </div>
      </header>

      {summary.isLoading && <p className="text-paper-trace">Loading…</p>}
      {summary.isError && !deskOff(summary.error) && <p className="text-red-400">Couldn't load the desk summary. Try again.</p>}

      {s && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-ink-500 border border-ink-500">
            <Stat label="Over target" value={String(overTarget)} tone={overTarget > 0 ? 'text-red-400' : undefined} />
            <Stat label="Oldest waiting" value={firstHours != null ? waitingLabel(firstHours) : '—'}
              sub={first ? `${TITLE_LABELS[first.publication ?? 'none']} · ${topicLabel(first.topic)}` : undefined} />
            <Stat label="Drafts ready" value={String(s.drafts_ready)} />
            <Stat label="Cancellation requests · 7 days" value={String(s.cancellations_7d)} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {[...TITLE_ORDER, 'none' as const].filter(b => b !== 'none' || s.titles.none?.to_review > 0).map(b => {
              const t = s.titles[b]
              if (!t) return null
              const h = hoursSince(t.oldest_waiting_at)
              return (
                <Link key={b} to={`/support/${b}`} className="border border-ink-500 p-4 hover:bg-ink-800 block">
                  <div className="label">{TITLE_LABELS[b]}</div>
                  <div className="font-serif text-4xl text-paper mt-1">{t.to_review}</div>
                  <div className="text-xs text-paper-trace mt-2">
                    {t.contact} contact · {t.feedback} feedback · {t.email} email
                  </div>
                  <div className="text-xs text-paper-dim mt-1">Oldest {h != null ? waitingLabel(h) : '—'}</div>
                </Link>
              )
            })}
          </div>
        </>
      )}

      <SupportQueue mine={mine} />
    </div>
  )
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="bg-ink-900 p-4">
      <div className="label">{label}</div>
      <div className={`font-serif text-3xl mt-1 ${tone ?? 'text-paper'}`}>{value}</div>
      {sub && <div className="text-xs text-paper-trace mt-1">{sub}</div>}
    </div>
  )
}
